<template>
  <div class="page">
    <div class="ann-head">
      <div>
        <h2 class="page-title">{{ $t('announcement.historyTitle') }}</h2>
        <div class="muted ann-subtitle">{{ $t('announcement.historyHint') }}</div>
      </div>
      <el-button size="small" @click="refresh" :loading="loading">{{ $t('common.refresh') }}</el-button>
    </div>

    <div class="glass-card ann-panel">
      <div v-if="loading" class="muted">{{ $t('common.loading') }}</div>
      <el-empty v-else-if="!items.length" :description="$t('announcement.empty')" />
      <div v-else class="ann-list">
        <article
          v-for="item in items"
          :key="item.id"
          :class="['ann-item', isExpanded(item.id) ? 'ann-item--expanded' : '']"
          role="button"
          tabindex="0"
          @click="toggleItem(item.id)"
          @keyup.enter="toggleItem(item.id)"
        >
          <div class="ann-item__main">
            <div class="ann-item__row">
              <div class="ann-item__title">{{ item.title }}</div>
              <div class="ann-item__meta">
                <span>{{ formatTime(item.publishedAt) }}</span>
                <span :class="['tag-pill', item.confirmed ? '' : 'tag-pill--warn']">
                  {{ item.confirmed ? $t('announcement.confirmed') : $t('announcement.required') }}
                </span>
              </div>
            </div>
            <div class="ann-item__content">{{ item.content }}</div>
          </div>
          <el-button
            v-if="!item.confirmed"
            size="mini"
            type="primary"
            class="btn-glass ann-item__action"
            @click.stop="confirm(item.id)"
          >
            {{ $t('common.confirm') }}
          </el-button>
          <i :class="['el-icon-arrow-down', 'ann-item__toggle', isExpanded(item.id) ? 'is-expanded' : '']" />
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import { secondaryListAnnouncements, secondaryConfirmAnnouncement } from '../../api/secondary';

export default {
  name: 'SecondaryAnnouncements',
  data() {
    return {
      loading: false,
      items: [],
      expandedIds: {}
    };
  },
  created() {
    this.refresh();
  },
  computed: {
    version() {
      return this.$store.state.versions.announcements;
    }
  },
  watch: {
    version() {
      this.refresh();
    }
  },
  methods: {
    async refresh() {
      this.loading = true;
      try {
        this.items = await secondaryListAnnouncements();
      } finally {
        this.loading = false;
      }
    },
    isExpanded(id) {
      return !!this.expandedIds[id];
    },
    toggleItem(id) {
      this.$set(this.expandedIds, id, !this.expandedIds[id]);
    },
    formatTime(value) {
      if (!value) return '-';
      try {
        return new Date(value).toLocaleString(this.$i18n.locale === 'zh' ? 'zh-CN' : 'en-US');
      } catch (e) {
        return value;
      }
    },
    async confirm(id) {
      await secondaryConfirmAnnouncement(id);
      this.$store.commit('bumpVersion', 'announcements');
      await this.refresh();
    }
  }
};
</script>

<style scoped>
.ann-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.ann-subtitle {
  font-size: 13px;
}
.ann-panel {
  padding: 14px;
}
.ann-list {
  display: grid;
  gap: 8px;
}
.ann-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 74px;
  padding: 12px 44px 12px 14px;
  border: 1px solid rgba(203, 213, 225, 0.75);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  box-sizing: border-box;
  overflow: hidden;
}
.ann-item__main {
  min-width: 0;
  flex: 1;
}
.ann-item__row {
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
}
.ann-item__title {
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ann-item__content {
  margin-top: 8px;
  line-height: 1.6;
  white-space: pre-wrap;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 13px;
}
.ann-item--expanded .ann-item__content {
  -webkit-line-clamp: 8;
}
.ann-item__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  color: var(--muted);
  font-size: 12px;
  white-space: nowrap;
}
.ann-item__action {
  flex: 0 0 auto;
}
.ann-item__toggle {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(81, 96, 118, 0.75);
  transition: transform 0.15s ease;
}
.ann-item__toggle.is-expanded {
  transform: translateY(-50%) rotate(180deg);
}
@media (max-width: 680px) {
  .ann-head,
  .ann-item {
    flex-direction: column;
    align-items: stretch;
  }
  .ann-item {
    padding-right: 38px;
  }
  .ann-item__row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
  .ann-item__meta {
    flex-wrap: wrap;
    white-space: normal;
  }
  .ann-item__title {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}
</style>
